import React, { createContext, useContext, useState, useCallback, ReactNode, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Comic, CustomList } from '../types';
import { getComics, getCustomLists } from '../api';
import { isAuthenticated } from '../api';

interface ComicsDataContextType {
  comics: Comic[];
  lists: CustomList[];
  loading: boolean;
  error: string | null;
  fetchComics: (customOrder?: boolean) => Promise<void>;
  fetchLists: () => Promise<void>;
  refreshData: () => Promise<void>;
  refreshAfterLogin: () => Promise<void>;
  setComics: React.Dispatch<React.SetStateAction<Comic[]>>;
  setLists: React.Dispatch<React.SetStateAction<CustomList[]>>;
}

const ComicsDataContext = createContext<ComicsDataContextType | undefined>(undefined);

export const useComicsData = () => {
  const context = useContext(ComicsDataContext);
  if (context === undefined) {
    throw new Error('useComicsData must be used within a ComicsDataProvider');
  }
  return context;
};

interface ComicsDataProviderProps {
  children: ReactNode;
}

export const ComicsDataProvider: React.FC<ComicsDataProviderProps> = ({ children }) => {
  const { t } = useTranslation();
  const [comics, setComics] = useState<Comic[]>([]);
  const [lists, setLists] = useState<CustomList[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const initializedRef = useRef(false);

  const fetchComics = useCallback(async (customOrder: boolean = false) => {
    
    try {
      setError(null);
      const fetchedComics = await getComics(customOrder);
      setComics(fetchedComics);
      
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : t('errors.failedToFetchComics');
      setError(errorMessage);
      
    }
  }, [t]);

  const fetchLists = useCallback(async () => {
    
    try {
      setError(null);
      const fetchedLists = await getCustomLists();
      setLists(fetchedLists);
      
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : t('errors.failedToFetchLists');
      setError(errorMessage);
      
    }
  }, [t]);

  const refreshData = useCallback(async () => {
    
    setLoading(true);
    try {
      // Check if custom order should be used based on saved sort preference
      const savedSortBy = localStorage.getItem('comicSortBy');
      const useCustomOrder = savedSortBy === 'custom';
      await Promise.all([fetchComics(useCustomOrder), fetchLists()]);
    } finally {
      setLoading(false);
    }
  }, [fetchComics, fetchLists]);

  const refreshAfterLogin = useCallback(async () => {
    
    initializedRef.current = true;
    await refreshData();
  }, [refreshData]);

  // Initial load when provider mounts (with guard against double-mounting in StrictMode)
  useEffect(() => {
    
    if (!initializedRef.current && isAuthenticated()) {
      
      initializedRef.current = true;
      refreshData();
    } else if (!isAuthenticated()) {
      
    } else {
      
    }
  }, [refreshData, comics.length, lists.length]);

  const value: ComicsDataContextType = {
    comics,
    lists,
    loading,
    error,
    fetchComics,
    fetchLists,
    refreshData,
    refreshAfterLogin,
    setComics,
    setLists,
  };

  return <ComicsDataContext.Provider value={value}>{children}</ComicsDataContext.Provider>;
};